import { apm } from '@elastic/apm-rum';
import { ConfigProvider, notification, Typography } from 'antd';
import { ConfigProvider as CountryPhoneNumberInputConfigProvider } from 'antd-country-phone-input';
import enUs from 'antd/lib/locale/en_US';
import esEs from 'antd/lib/locale/es_ES';
import ptBr from 'antd/lib/locale/pt_BR';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import i18next from 'i18next';
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/pt-br';
import React, { useEffect, useMemo } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import br from 'world_countries_lists/data/countries/en/world.json';

import useAPIClient, { APIClient } from '../api/useAPIClient';
import { errorResultDefaults, ErrorResultStatusType } from '../components/ErrorResult';
import useTypedParams from '../hooks/useTypedParams';
import { t } from '../i18n';

const throwApiNotificationError = (
  requestError: AxiosError,
  title: React.ReactNode,
  subTitle: React.ReactNode
) => {
  notification.error({
    duration: 0,
    message: title || t('Erro'),
    description: (
      <>
        {subTitle}
        {requestError.response?.headers['x-elasticapm-traceid'] && (
          <>
            <br />
            <br />
            <Typography.Text code>
              Trace ID: {requestError.response?.headers['x-elasticapm-traceid']}
            </Typography.Text>
          </>
        )}
      </>
    ),
  });
};

const createQueryClient = (tenant: string, api: APIClient) => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        queryFn: async ({ queryKey, signal }) => {
          const [route, requestConfig] = queryKey as [string, AxiosRequestConfig];

          const { data } = await api.request({
            url: `${route}`,
            method: 'GET',
            signal,
            ...requestConfig,
          });

          return data;
        },
        onError: (err) => {
          if (!axios.isAxiosError(err) || (err.response && err.response.status >= 500)) {
            apm.captureError(err as Error);
          }

          if (axios.isAxiosError(err) && err.response && err.response.status === 401) {
            const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || window.location.origin;
            window.location.href = `${LOGIN_URL}/${tenant}/accounts/login/?next=${window.location.href}`;
          }
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        onError: (error) => {
          apm.captureError(error as Error);

          const status = (error as AxiosError).response?.status.toString() as ErrorResultStatusType;
          const { title, subTitle } = errorResultDefaults[status || 'error'] || {};
          throwApiNotificationError(error as AxiosError, title, subTitle);
        },
      },
    },
  });
};

const convertAntdLocale = (language: string) => {
  switch (language) {
    case 'en':
      return enUs;
    case 'es':
      return esEs;
    default:
      return ptBr;
  }
};

export default function AppProvider({ children }: React.PropsWithChildren<object>) {
  const { tenant } = useTypedParams({ tenant: String });
  const client = useAPIClient();
  const i18nLanguage = i18next.language;

  const antdLocale = convertAntdLocale(i18nLanguage);

  useEffect(() => {
    if (i18next.language === 'pt') {
      moment.locale('pt-br');
    } else {
      moment.locale(i18next.language);
    }
  }, []);

  const queryClient = useMemo(() => createQueryClient(tenant, client), [client, tenant]);

  return (
    <ConfigProvider locale={antdLocale}>
      <QueryClientProvider client={queryClient}>
        <CountryPhoneNumberInputConfigProvider locale={br}>
          <HelmetProvider>{children}</HelmetProvider>
        </CountryPhoneNumberInputConfigProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ConfigProvider>
  );
}
